import * as Sentry from '@sentry/react'
import {
  ArrowLeft,
  User,
  AlertCircle,
  RefreshCw,
  Clock,
  XCircle,
  WifiOff,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'

import BeneficiaryEditDrawer from '@/components/ui/BeneficiaryEditDrawer'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import QuoteTimer from '@/components/ui/QuoteTimer'
import SenderEditDrawer from '@/components/ui/SenderEditDrawer'
import { cpnService } from '@/services/cpn'
import {
  CpnBeneficiaryUser,
  CpnQuote,
  SwapQuoteResponse,
  SwapPaymentResponse,
  CpnRequirementsResponse,
} from '@/types/cpn'
import { TransferData } from '@/types/payment'
import {
  type LockedQuoteData,
  type FormattedQuote,
  isRealSwapQuoteData,
} from '@/types/quote'
import { cadToDestinationRate } from '@/utils/currencyUtils'
import { logger } from '@/utils/logger'
import { savePaymentPreference } from '@/utils/paymentPreferences'

import RequirementsDisplayCard from './RequirementsDisplayCard'

type ErrorCategory = 'rate_limit' | 'network' | 'validation' | 'generic'

type ErrorDisplayInfo = {
  category: ErrorCategory
  title: string
  message: string
  suggestion?: string
  icon: typeof Clock | typeof WifiOff | typeof AlertCircle | typeof XCircle
}

const categorizeError = (errorMessage: string): ErrorDisplayInfo => {
  const lowerError = errorMessage.toLowerCase()

  // Too many active/pending transactions
  if (
    lowerError.includes('too many active') ||
    lowerError.includes('complete or cancel')
  ) {
    return {
      category: 'rate_limit',
      title: 'Too Many Pending Transactions',
      message: errorMessage,
      suggestion:
        'Please complete or cancel your existing transactions before starting a new one.',
      icon: AlertCircle,
    }
  }

  // Daily limit / too many cancelled/expired
  if (
    lowerError.includes('too many cancelled') ||
    lowerError.includes('expired transactions') ||
    lowerError.includes('try again tomorrow') ||
    lowerError.includes('daily limit')
  ) {
    return {
      category: 'rate_limit',
      title: 'Daily Limit Reached',
      message: errorMessage,
      suggestion: 'Your account has reached its daily transaction limit.',
      icon: Clock,
    }
  }

  // Network errors
  if (
    lowerError.includes('network') ||
    lowerError.includes('timeout') ||
    lowerError.includes('connection') ||
    lowerError.includes('failed to fetch')
  ) {
    return {
      category: 'network',
      title: 'Connection Error',
      message: errorMessage,
      suggestion: 'Please check your internet connection and try again.',
      icon: WifiOff,
    }
  }

  // Validation errors
  if (
    lowerError.includes('invalid') ||
    lowerError.includes('required') ||
    lowerError.includes('missing')
  ) {
    return {
      category: 'validation',
      title: 'Validation Error',
      message: errorMessage,
      icon: AlertCircle,
    }
  }

  // Generic fallback
  return {
    category: 'generic',
    title: 'Payment Error',
    message: errorMessage,
    suggestion: 'If this problem persists, please contact support.',
    icon: XCircle,
  }
}

interface PaymentConfirmationScreenProps {
  transferData: TransferData
  beneficiary: CpnBeneficiaryUser
  onBack: () => void
  onPaymentSuccess: () => void
  userId: string
  quote?: CpnQuote
  formattedQuote?: FormattedQuote
}

const PaymentConfirmationScreen = ({
  transferData,
  beneficiary,
  onBack,
  onPaymentSuccess,
  userId,
  quote,
  formattedQuote,
}: PaymentConfirmationScreenProps) => {
  const [autoRefreshEnabled] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [intentId, setIntentId] = useState<string | null>(null)
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [isCreatingPayment, setIsCreatingPayment] = useState(false)
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false)
  const [isSenderEditDrawerOpen, setIsSenderEditDrawerOpen] = useState(false)
  const [lockedQuote, setLockedQuote] = useState<FormattedQuote | null>(null)
  const [isQuoteRealTimeExpired, setIsQuoteRealTimeExpired] = useState(false)
  const [isValidatingRequirements, setIsValidatingRequirements] =
    useState(false)
  const [storedQuoteData, setStoredQuoteData] =
    useState<LockedQuoteData | null>(null)
  const [requirementsData, setRequirementsData] =
    useState<CpnRequirementsResponse | null>(null)

  // Helper function to get locked quote from storage
  const getLockedQuoteFromStorage = (): LockedQuoteData | null => {
    try {
      const lockedQuoteData = sessionStorage.getItem('lockedQuoteData')
      if (!lockedQuoteData) return null

      const quoteData = JSON.parse(lockedQuoteData) as LockedQuoteData
      return quoteData
    } catch (error) {
      logger.error('Failed to parse locked quote data', error)
      return null
    }
  }

  // Function to load and parse stored quote data
  const loadStoredQuoteData = useCallback((): FormattedQuote | null => {
    const quoteData = getLockedQuoteFromStorage()

    setStoredQuoteData(quoteData)

    if (!quoteData) return null

    // Handle new format (real swap quote response with cpn.quotes data)
    if (isRealSwapQuoteData(quoteData) && quoteData.cpn?.quotes?.[0]) {
      const cpnQuote = quoteData.cpn.quotes[0]

      // Parse the real quote data from cpn.quotes
      const sourceAmount = parseFloat(cpnQuote.sourceAmount)
      const destinationAmount = parseFloat(cpnQuote.destinationAmount)
      const exchangeRate = parseFloat(cpnQuote.exchangeRate)
      const feeAmount = parseFloat(cpnQuote.feeAmount || '0')

      // Convert UTC expiry date to local time
      const expiryDateLocal = new Date(cpnQuote.quoteExpireDate)

      const formatted: FormattedQuote = {
        sourceAmount,
        destinationAmount,
        exchangeRate,
        fees: {
          amount: feeAmount,
          currency: cpnQuote.feeCurrency || 'USDC',
          percentage: sourceAmount > 0 ? (feeAmount / sourceAmount) * 100 : 0,
        },
        expiresAt: expiryDateLocal,
        paymentMethod: cpnQuote.paymentMethodType,
        quoteType:
          (quoteData.cpn.quoteType as 'fromExact' | 'toExact') || 'fromExact',
      }

      return formatted
    }

    // Fallback to original quote for backward compatibility
    if (isRealSwapQuoteData(quoteData) && quoteData.originalQuote) {
      const formatted: FormattedQuote = {
        sourceAmount: quoteData.originalQuote.sourceAmount,
        destinationAmount: quoteData.originalQuote.destinationAmount,
        exchangeRate: quoteData.originalQuote.exchangeRate,
        fees: quoteData.originalQuote.fees,
        expiresAt: new Date(quoteData.originalQuote.expiresAt),
        paymentMethod: quoteData.originalQuote.paymentMethod,
        quoteType: quoteData.originalQuote.quoteType || 'fromExact',
      }
      return formatted
    }

    return null
  }, [])

  // Get locked quote from session storage on component mount
  useEffect(() => {
    const locked = loadStoredQuoteData()
    if (locked) {
      setLockedQuote(locked)
    }
  }, [loadStoredQuoteData])

  const checkPaymentRequirements = useCallback(async (): Promise<void> => {
    if (!quote) {
      logger.warn('No quote available for requirements check')
      return
    }

    setIsValidatingRequirements(true)
    setError(null)

    try {
      const requirementsResponse = await cpnService.getPaymentRequirements(
        quote.baseQuoteUuid,
        beneficiary.uuid
      )

      setRequirementsData(requirementsResponse)
    } catch (err) {
      logger.error('Failed to check requirements', err)
      let errorMessage = 'Failed to check requirements'
      if (err?.response?.data) {
        errorMessage =
          typeof err.response.data === 'string'
            ? err.response.data
            : err.response.data.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)

      Sentry.captureException(err, {
        level: 'error',
        tags: {
          component: 'PaymentConfirmationScreen',
          action: 'checkPaymentRequirements',
        },
        extra: {
          errorMessage,
          quoteId: quote.baseQuoteUuid,
          beneficiaryUuid: beneficiary.uuid,
        },
      })
    } finally {
      setIsValidatingRequirements(false)
    }
  }, [quote, beneficiary.uuid])

  // Check requirements when component mounts and we have a quote
  useEffect(() => {
    if (quote) {
      checkPaymentRequirements()
    }
  }, [quote, checkPaymentRequirements])

  // Refresh quote on mount if beneficiary is self to ensure isFirstParty is set
  useEffect(() => {
    if (beneficiary?.isSelf) {
      handleRefreshQuote(true)
    }
  }, [beneficiary?.isSelf])

  const handleRefreshQuote = useCallback(
    async (isAutoRefresh = false) => {
      if (isRefreshingQuote) {
        return
      }

      setIsRefreshingQuote(true)
      setError(null)

      try {
        const storedQuoteData = getLockedQuoteFromStorage()

        // We need the original quote data to get the source amount
        if (!storedQuoteData?.originalQuote) {
          throw new Error('No original quote data found for refresh')
        }

        if (!beneficiary?.uuid) {
          throw new Error('No beneficiary available for quote refresh')
        }
        // Step 1: Get fresh quote data using the same flow as SendMoneyScreen
        // Preserve the original quote type to maintain toExact behavior
        const originalQuoteType =
          storedQuoteData.originalQuote.quoteType || 'fromExact'
        const quoteParams = {
          quoteType: originalQuoteType,
          senderCountry: 'CA',
          sourceCurrency: 'CAD',
          amount:
            originalQuoteType === 'toExact'
              ? storedQuoteData.originalQuote.destinationAmount.toString()
              : storedQuoteData.originalQuote.sourceAmount.toString(),
          destinationCountry: transferData.receiveCountry,
          destinationCurrency: transferData.receiveCurrency,
          paymentMethod: transferData.paymentMethod,
        }

        const freshQuoteResponse = await cpnService.getLatestQuote(quoteParams)

        // Extract quote data like in useTransferWithQuotes
        const cpnQuotes = freshQuoteResponse?.cpn
        const selectedQuote =
          cpnQuotes?.find(
            (quote) => quote.paymentMethod === transferData.paymentMethod
          ) || cpnQuotes?.[0]

        if (!selectedQuote || !freshQuoteResponse?.paytrie) {
          throw new Error('No valid quote received from server')
        }

        const paytrieData = freshQuoteResponse.paytrie
        const destinationAmount = parseFloat(selectedQuote.estDestinationAmount)
        const usdcToDestinationRate = parseFloat(selectedQuote.cpnExchangeRate)
        const cadToUsdcRate = paytrieData.cadUsdc
        const exchangeRate = cadToDestinationRate(
          cadToUsdcRate,
          usdcToDestinationRate
        )

        const cpnFeeUsdc = parseFloat(selectedQuote.estCpnFee)
        const cadFee = paytrieData.cadFee + cpnFeeUsdc * cadToUsdcRate

        // Create fresh quote formatted data - handle both fromExact and toExact
        let sourceAmount, destAmount
        if (originalQuoteType === 'toExact') {
          // For toExact: user specified destination amount, we calculate source amount
          destAmount = parseFloat(quoteParams.amount) // This is the destination amount user wanted
          // Use the CAD amount from paytrie data as the source amount for toExact
          sourceAmount = paytrieData.cadAmount
        } else {
          // For fromExact: user specified source amount, we calculate destination amount
          sourceAmount = parseFloat(quoteParams.amount) // This is the source amount user specified
          destAmount = destinationAmount // Server calculated destination amount
        }

        // Validate that we have valid amounts
        if (!sourceAmount || isNaN(sourceAmount) || sourceAmount <= 0) {
          throw new Error(
            `Invalid source amount: ${sourceAmount}. Cannot create swap quote.`
          )
        }
        if (!destAmount || isNaN(destAmount) || destAmount <= 0) {
          throw new Error(
            `Invalid destination amount: ${destAmount}. Cannot create swap quote.`
          )
        }

        const feePercentage = (cadFee / sourceAmount) * 100

        const freshFormattedQuote = {
          sourceAmount,
          destinationAmount: destAmount,
          exchangeRate,
          cadUsdcRate: cadToUsdcRate,
          fees: {
            amount: cadFee,
            currency: 'CAD',
            percentage: feePercentage,
          },
          expiresAt: new Date(selectedQuote.quoteExpireDate),
          paymentMethod: selectedQuote.paymentMethod,
          quoteType: originalQuoteType,
        }

        // Step 2: Create swap quote with the fresh data
        const swapQuotePayload = {
          leftSideValue: freshFormattedQuote.sourceAmount,
          destinationCountry: transferData.receiveCountry,
          destinationCurrency: transferData.receiveCurrency,
          paymentMethod: transferData.paymentMethod,
          beneficiaryRefId: beneficiary.uuid,
          ...(beneficiary.isSelf && { isFirstParty: true }),
        }

        const swapQuoteResult =
          await cpnService.createSwapQuote(swapQuotePayload)

        // Use the fresh quote data for display, swap quote response for intent ID
        const refreshedQuote: FormattedQuote = {
          sourceAmount: freshFormattedQuote.sourceAmount,
          destinationAmount: freshFormattedQuote.destinationAmount,
          exchangeRate: freshFormattedQuote.exchangeRate,
          fees: freshFormattedQuote.fees,
          expiresAt: freshFormattedQuote.expiresAt,
          paymentMethod: freshFormattedQuote.paymentMethod,
          quoteType: freshFormattedQuote.quoteType,
        }

        // Store refreshed quote data FIRST - same format as handleBeneficiarySelected
        const refreshedQuoteData = {
          ...swapQuoteResult,
          originalQuote: freshFormattedQuote, // Use fresh quote as new original
          lockedAt: new Date().toISOString(),
          isRealSwapQuote: true,
          swapQuotePayload: swapQuotePayload,
        }

        sessionStorage.setItem(
          'lockedQuoteData',
          JSON.stringify(refreshedQuoteData)
        )
        sessionStorage.setItem('currentIntentId', swapQuoteResult.id)

        // Reload stored quote data to update storedQuoteData state
        loadStoredQuoteData()

        // Update all local state to trigger re-renders
        setLockedQuote(refreshedQuote)
        setIntentId(swapQuoteResult.id)
        setIsQuoteRealTimeExpired(false) // Reset expiration state

        // Force re-check of requirements with new quote
        if (quote) {
          await checkPaymentRequirements()
        }
      } catch (err) {
        logger.error('Failed to refresh quote', err)
        let errorMessage = 'Failed to refresh quote'
        if (err?.response?.data) {
          errorMessage =
            typeof err.response.data === 'string'
              ? err.response.data
              : err.response.data.message
        } else if (err instanceof Error) {
          errorMessage = err.message
        }
        setError(errorMessage)

        Sentry.captureException(err, {
          level: 'error',
          tags: {
            component: 'PaymentConfirmationScreen',
            action: 'handleRefreshQuote',
            isAutoRefresh: isAutoRefresh.toString(),
          },
          extra: {
            errorMessage,
            sendAmount: displaySourceAmount,
            sendCurrency: transferData.sendCurrency,
            receiveAmount: displayDestinationAmount,
            receiveCurrency: transferData.receiveCurrency,
            beneficiaryUuid: beneficiary.uuid,
          },
        })
      } finally {
        setIsRefreshingQuote(false)
      }
    },
    [
      beneficiary,
      transferData,
      loadStoredQuoteData,
      quote,
      checkPaymentRequirements,
      isRefreshingQuote,
    ]
  )

  const handleManageBeneficiary = () => {
    setIsEditDrawerOpen(true)
  }

  const handleBeneficiaryUpdated = () => {
    // Refresh requirements check after beneficiary is updated
    if (quote) {
      checkPaymentRequirements()
    }
  }

  const handleManageUser = () => {
    setIsSenderEditDrawerOpen(true)
  }

  const handleSenderUpdated = () => {
    // Refresh requirements check after sender info is updated
    if (quote) {
      checkPaymentRequirements()
    }
  }

  const handleSendPayment = async (): Promise<void> => {
    setIsCreatingPayment(true)
    setError(null)

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Payment initiation started',
      level: 'info',
      data: {
        sendAmount: displaySourceAmount,
        sendCurrency: transferData.sendCurrency,
        receiveAmount: displayDestinationAmount,
        receiveCurrency: transferData.receiveCurrency,
        paymentMethod: transferData.paymentMethod,
        beneficiaryUuid: beneficiary.uuid,
      },
    })

    Sentry.setContext('transaction', {
      sendAmount: displaySourceAmount,
      sendCurrency: transferData.sendCurrency,
      receiveAmount: displayDestinationAmount,
      receiveCurrency: transferData.receiveCurrency,
      receiveCountry: transferData.receiveCountry,
      paymentMethod: transferData.paymentMethod,
      exchangeRate: displayExchangeRate,
      fees: displayFees.amount,
      feeCurrency: displayFees.currency,
    })

    Sentry.setContext('beneficiary', {
      uuid: beneficiary.uuid,
      isBusiness: beneficiary.isBusiness,
      country: beneficiary.country,
    })

    try {
      const existingQuote = getLockedQuoteFromStorage()
      let finalIntentId = intentId

      if (existingQuote && isRealSwapQuoteData(existingQuote)) {
        finalIntentId =
          existingQuote.id || sessionStorage.getItem('currentIntentId')
        setIntentId(finalIntentId)
      } else {
        const swapQuotePayload = {
          leftSideValue: parseFloat(displaySourceAmount),
          destinationCountry: transferData.receiveCountry,
          destinationCurrency: transferData.receiveCurrency,
          paymentMethod: transferData.paymentMethod,
          beneficiaryRefId: beneficiary.uuid,
          ...(beneficiary.isSelf && { isFirstParty: true }),
        }
        const swapQuoteResult: SwapQuoteResponse =
          await cpnService.createSwapQuote(swapQuotePayload)

        const realQuoteData = {
          ...swapQuoteResult,
          originalQuote: existingQuote?.originalQuote || formattedQuote,
          lockedAt: new Date().toISOString(),
          isRealSwapQuote: true,
        }
        sessionStorage.setItem('lockedQuoteData', JSON.stringify(realQuoteData))
        sessionStorage.setItem('currentIntentId', swapQuoteResult.id)

        finalIntentId = swapQuoteResult.id
        setIntentId(finalIntentId)
      }

      if (!finalIntentId) {
        throw new Error('No intent ID available for payment processing')
      }

      await handleCreateTransaction(finalIntentId)
    } catch (err) {
      logger.error('Payment processing failed', err)
      let errorMessage = 'Payment failed'
      const errorObj = err as {
        response?: { data?: string | { message?: string } }
      }
      if (errorObj?.response?.data) {
        errorMessage =
          typeof errorObj.response.data === 'string'
            ? errorObj.response.data
            : (errorObj.response.data.message ?? errorMessage)
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)

      Sentry.captureException(err, {
        level: 'error',
        tags: {
          component: 'PaymentConfirmationScreen',
          action: 'handleSendPayment',
          paymentMethod: transferData.paymentMethod,
          currency: transferData.sendCurrency,
        },
        extra: {
          errorMessage,
          sendAmount: displaySourceAmount,
          sendCurrency: transferData.sendCurrency,
          receiveAmount: displayDestinationAmount,
          receiveCurrency: transferData.receiveCurrency,
          receiveCountry: transferData.receiveCountry,
          paymentMethod: transferData.paymentMethod,
          beneficiaryUuid: beneficiary.uuid,
          exchangeRate: displayExchangeRate,
          fees: displayFees,
        },
      })
    } finally {
      setIsCreatingPayment(false)
    }
  }

  const handleCreateTransaction = async (
    currentIntentId?: string
  ): Promise<void> => {
    try {
      const useCase = beneficiary.isBusiness
        ? ('C2B' as const)
        : ('C2C' as const)

      const swapPaymentPayload = {
        leftSideLabel: 'CAD',
        leftSideValue: parseFloat(displaySourceAmount),
        intentUuid: currentIntentId || intentId,
        useCase,
        reasonForPayment: beneficiary.paymentReason,
        useReferral: false,
      }

      const swapPaymentResult: SwapPaymentResponse =
        await cpnService.createSwapPayment(swapPaymentPayload)

      if (swapPaymentResult.tx) {
        sessionStorage.removeItem('currentPaymentId')
        sessionStorage.setItem('currentTransactionId', swapPaymentResult.tx)
        sessionStorage.setItem('cpnQuoteId', swapPaymentResult.cpnQuoteId)
        sessionStorage.setItem('currentIntentId', currentIntentId || intentId)

        savePaymentPreference(userId, {
          receiveCountry: transferData.receiveCountry,
          receiveCurrency: transferData.receiveCurrency,
          paymentMethod: transferData.paymentMethod,
        })

        onPaymentSuccess()
      } else {
        throw new Error('No transaction ID returned from server')
      }
    } catch (err) {
      logger.error('Transaction creation failed', err)
      let errorMessage = 'Transaction creation failed'
      const errorObj = err as {
        response?: { data?: string | { message?: string } }
      }
      if (errorObj?.response?.data) {
        errorMessage =
          typeof errorObj.response.data === 'string'
            ? errorObj.response.data
            : (errorObj.response.data.message ?? errorMessage)
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      setError(errorMessage)

      Sentry.captureException(err, {
        level: 'error',
        tags: {
          component: 'PaymentConfirmationScreen',
          action: 'handleCreateTransaction',
          intentId: currentIntentId || intentId || 'unknown',
        },
        extra: {
          errorMessage,
          intentId: currentIntentId || intentId,
          useCase: beneficiary.isBusiness ? 'C2B' : 'C2C',
          amount: displaySourceAmount,
          currency: transferData.sendCurrency,
        },
      })

      throw err
    }
  }

  const formatCurrency = (amount: string, currency: string) => {
    return `${amount} ${currency}`
  }

  // Helper to check if quote is expired (for styling purposes)
  const isQuoteExpired = (expiresAt: Date | string): boolean => {
    const now = Date.now() // Use Date.now() for consistency with QuoteTimer
    const expiry = new Date(expiresAt).getTime()
    return expiry <= now
  }

  // Handle when timer expires
  const handleQuoteExpired = useCallback(() => {
    setIsQuoteRealTimeExpired(true)

    // Auto-refresh if enabled and not already refreshing
    if (autoRefreshEnabled && !isRefreshingQuote && !isCreatingPayment) {
      handleRefreshQuote(true) // true = auto-refresh
    }
  }, [
    autoRefreshEnabled,
    isRefreshingQuote,
    isCreatingPayment,
    handleRefreshQuote,
  ])

  // Use locked quote if available, otherwise fall back to formattedQuote or transferData
  const activeQuote = lockedQuote || formattedQuote
  const isRealSwapQuote =
    storedQuoteData && isRealSwapQuoteData(storedQuoteData)

  // For display amounts, use original CAD values from stored data when available
  const originalQuoteData = storedQuoteData?.originalQuote
  const displaySourceAmount = originalQuoteData
    ? originalQuoteData.sourceAmount.toFixed(2)
    : activeQuote
      ? activeQuote.sourceAmount.toFixed(2)
      : transferData.sendAmount
  const displayDestinationAmount = originalQuoteData
    ? originalQuoteData.destinationAmount.toFixed(2)
    : activeQuote
      ? activeQuote.destinationAmount.toFixed(2)
      : transferData.receiveAmount
  const displayExchangeRate = originalQuoteData
    ? originalQuoteData.exchangeRate
    : activeQuote
      ? activeQuote.exchangeRate
      : transferData.exchangeRate || 0.7298
  const displayFees = originalQuoteData
    ? originalQuoteData.fees
    : activeQuote
      ? activeQuote.fees
      : {
          amount: transferData.fees,
          currency: transferData.sendCurrency,
          percentage: 0,
        }
  const displayFeeCurrency = displayFees.currency

  return (
    <div className="relative h-[600px] flex flex-col">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Confirm Payment
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Review and send your transfer
            </p>
          </div>
        </div>

        {/* Transfer Summary */}
        <div className="bg-blue-50 dark:bg-slate-800 rounded-xl p-6">
          <div className="flex items-center space-x-2 mb-4">
            <h3 className="font-semibold text-blue-900 dark:text-white text;sm">
              Transfer Amount
            </h3>
          </div>

          <div className="text-center py-2">
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {formatCurrency(displaySourceAmount, transferData.sendCurrency)}
            </div>
            <div className="text-gray-700 dark:text-gray-300 text-base mb-1">
              {beneficiary.firstName || 'Recipient'} receives:{' '}
              {formatCurrency(
                displayDestinationAmount,
                transferData.receiveCurrency
              )}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-xs">
              Paying with {transferData.paymentMethod}
            </div>
          </div>
        </div>

        {/* Quote Information - Show locked quote if available */}
        {activeQuote && (
          <Card
            className={`p-4 ${
              isQuoteExpired(activeQuote.expiresAt) || isQuoteRealTimeExpired
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <AlertCircle
                  className={`w-4 h-4 ${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-600'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                />
                <h3
                  className={`font-medium ${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {isRealSwapQuote
                    ? 'Swap Quote Details'
                    : lockedQuote
                      ? 'Locked Quote Details'
                      : 'Quote Details'}
                </h3>
              </div>

              {/* Refresh Quote Button - Show for expired quotes or real swap quotes */}
              {(isRealSwapQuote ||
                isQuoteExpired(activeQuote.expiresAt) ||
                isQuoteRealTimeExpired) && (
                <Button
                  onClick={() => handleRefreshQuote(false)}
                  disabled={
                    isRefreshingQuote ||
                    isCreatingPayment ||
                    isValidatingRequirements
                  }
                  size="sm"
                  variant="ghost"
                  className={`h-8 w-8 p-0 ${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                  title={
                    isRefreshingQuote ? 'Refreshing quote...' : 'Refresh quote'
                  }
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isRefreshingQuote ? 'animate-spin' : ''}`}
                  />
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {/* {(quote || realCpnQuote) && (
                <div className="flex justify-between text-sm">
                  <span className={`${
                    getTimeUntilExpiry(getExpiryString(activeQuote.expiresAt)) === 'Quote expired' 
                      ? 'text-red-700 dark:text-red-300' 
                      : 'text-yellow-700 dark:text-yellow-300'
                  }`}>Quote ID:</span>
                  <span className={`font-mono text-xs ${
                    getTimeUntilExpiry(getExpiryString(activeQuote.expiresAt)) === 'Quote expired' 
                      ? 'text-red-900 dark:text-red-100' 
                      : 'text-yellow-900 dark:text-yellow-100'
                  }`}>{realCpnQuote?.quoteUuid || quote?.baseQuoteUuid}</span>
                </div>
              )} */}
              <div className="flex justify-between text-sm">
                <span
                  className={`${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Exchange Rate:
                </span>
                <span
                  className={`${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  1 CAD = {displayExchangeRate.toFixed(4)}{' '}
                  {transferData.receiveCurrency}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span
                  className={`${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Fees:
                </span>
                <div className="flex items-center space-x-2">
                  <span
                    className={`${
                      isQuoteExpired(activeQuote.expiresAt) ||
                      isQuoteRealTimeExpired
                        ? 'text-red-900 dark:text-red-100'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {displayFees.amount.toFixed(2)} {displayFeeCurrency}
                  </span>
                  {displayFees.percentage > 0 && (
                    <span
                      className={`text-xs ${
                        isQuoteExpired(activeQuote.expiresAt) ||
                        isQuoteRealTimeExpired
                          ? 'text-red-500 dark:text-red-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      ({displayFees.percentage.toFixed(1)}%)
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span
                  className={`${
                    isQuoteExpired(activeQuote.expiresAt) ||
                    isQuoteRealTimeExpired
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Quote Expires:
                </span>
                <div className="text-right">
                  <QuoteTimer
                    expiresAt={activeQuote.expiresAt}
                    className="text-xs font-medium"
                    onExpired={handleQuoteExpired}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Payment Requirements Check */}
        {isValidatingRequirements && (
          <Card className="p-4 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Validating payment requirements...
              </span>
            </div>
          </Card>
        )}

        {requirementsData && (
          <RequirementsDisplayCard
            missingRequirements={requirementsData.missingRequirements}
            onManageBeneficiary={handleManageBeneficiary}
            onManageUser={handleManageUser}
            isLoading={isCreatingPayment}
          />
        )}

        {/* Recipient Details */}
        <Card className="p-4">
          <div className="flex items-center space-x-2 mb-3">
            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Recipient
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Name:
              </span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {beneficiary.firstName} {beneficiary.lastName}
              </span>
            </div>
            {beneficiary.city && beneficiary.country && (
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Location:
                </span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {beneficiary.city}, {beneficiary.country}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Country:
              </span>
              <span className="text-sm text-gray-900 dark:text-white">
                {transferData.receiveCountry}
              </span>
            </div>
          </div>
        </Card>

        {/* Error Display */}
        {error &&
          (() => {
            const errorInfo = categorizeError(error)
            const IconComponent = errorInfo.icon
            const isRateLimit = errorInfo.category === 'rate_limit'

            return (
              <Card
                className={`p-4 ${
                  isRateLimit
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className={`flex-shrink-0 p-2 rounded-full ${
                      isRateLimit
                        ? 'bg-amber-100 dark:bg-amber-800/50'
                        : 'bg-red-100 dark:bg-red-800/50'
                    }`}
                  >
                    <IconComponent
                      className={`w-5 h-5 ${
                        isRateLimit
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <h4
                      className={`text-sm font-semibold ${
                        isRateLimit
                          ? 'text-amber-800 dark:text-amber-200'
                          : 'text-red-800 dark:text-red-200'
                      }`}
                    >
                      {errorInfo.title}
                    </h4>
                    <p
                      className={`text-sm ${
                        isRateLimit
                          ? 'text-amber-700 dark:text-amber-300'
                          : 'text-red-700 dark:text-red-300'
                      }`}
                    >
                      {errorInfo.message}
                    </p>
                    {errorInfo.suggestion && (
                      <p
                        className={`text-xs mt-2 ${
                          isRateLimit
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {errorInfo.suggestion}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            )
          })()}
      </div>

      {/* Send Payment Button */}
      {(isValidatingRequirements ||
        !requirementsData ||
        (requirementsData &&
          requirementsData.missingRequirements.travelRule.length === 0 &&
          requirementsData.missingRequirements.beneficiaryAccount.length ===
            0)) && (
        <div className="p-4 border-t border-gray-100 dark:border-gray-700">
          <Button
            onClick={handleSendPayment}
            disabled={isCreatingPayment || isValidatingRequirements}
            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreatingPayment ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Processing Payment...</span>
              </div>
            ) : isValidatingRequirements ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Checking Requirements...</span>
              </div>
            ) : isRealSwapQuote ? (
              `Send ${formatCurrency(displaySourceAmount, transferData.sendCurrency)}`
            ) : (
              `Send ${formatCurrency(displaySourceAmount, transferData.sendCurrency)}`
            )}
          </Button>
        </div>
      )}

      {/* Beneficiary Edit Drawer */}
      <BeneficiaryEditDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        beneficiary={beneficiary}
        onBeneficiaryUpdated={handleBeneficiaryUpdated}
        missingRequirements={requirementsData?.missingRequirements}
      />

      {/* Sender Edit Drawer */}
      <SenderEditDrawer
        isOpen={isSenderEditDrawerOpen}
        onClose={() => setIsSenderEditDrawerOpen(false)}
        missingRequirements={
          requirementsData?.missingRequirements.travelRule || []
        }
        beneficiaryUuid={beneficiary.uuid}
        onSenderUpdated={handleSenderUpdated}
      />
    </div>
  )
}

export default PaymentConfirmationScreen
